import React from 'react';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from './ui/hover-card';
import { usePointerDragOffset } from '../hooks/use-pointer-drag-offset';
import { isHoverCardDragIgnoredTarget } from '../utils/hover-card-drag';
import { cn } from '@/lib/utils';

type MarkHoverCardShellProps = {
  hoveredPosition: { top: number; left: number };
  hoverCardContentRef: React.RefObject<HTMLDivElement>;
  onClose: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  contentClassName?: string;
  children: React.ReactNode;
};

export const MarkHoverCardShell: React.FC<MarkHoverCardShellProps> = ({
  hoveredPosition,
  hoverCardContentRef,
  onClose,
  onMouseEnter,
  onMouseLeave,
  contentClassName,
  children,
}) => {
  const resetKey = `${hoveredPosition.top}:${hoveredPosition.left}`;
  const { offset, isDragging, isDragSessionActive, onDragHandleMouseDown } =
    usePointerDragOffset(resetKey);

  const handleClose = (): void => {
    if (isDragSessionActive) {
      return;
    }
    onClose();
  };

  const handleMouseLeave = (): void => {
    if (isDragSessionActive) {
      return;
    }
    onMouseLeave();
  };

  const handleCardMouseDown = (event: React.MouseEvent): void => {
    if (isHoverCardDragIgnoredTarget(event.target)) {
      return;
    }
    onDragHandleMouseDown(event);
  };

  return (
    <HoverCard open={true}>
      <HoverCardTrigger asChild>
        <div
          style={{
            position: 'fixed',
            top: hoveredPosition.top,
            left: hoveredPosition.left,
            width: 1,
            height: 1,
            pointerEvents: 'none',
            opacity: 0,
            zIndex: -1,
          }}
        />
      </HoverCardTrigger>
      <HoverCardContent
        ref={hoverCardContentRef}
        className={contentClassName}
        side="bottom"
        align="center"
        sideOffset={0}
        collisionPadding={8}
        onPointerDownOutside={handleClose}
        onMouseEnter={onMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div
          className={cn(
            'cursor-grab active:cursor-grabbing',
            isDragging && 'cursor-grabbing select-none',
          )}
          style={{
            transform: `translate(${offset.x}px, ${offset.y}px)`,
            touchAction: isDragging ? 'none' : undefined,
          }}
          onMouseDown={handleCardMouseDown}
        >
          {children}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};
